'use client';

import {twJoin} from 'tailwind-merge';
import {AnimatePresence} from 'motion/react';
import type {Map as MapboxMap} from 'mapbox-gl';
import type {VisitedStats} from 'server/apiHandlers/getVisitedStatsHandler';
import useSearchParam, {setSearchParam} from 'src/hooks/useSearchParam';
import MapReviewedPlacesList from './MapReviewedPlacesList';
import MapCountriesStats from './MapCountriesStats';
import {TabBar, TabButton} from '../common/Tabs';
import PlaceListModal from './PlaceListModal';
import pluralize from 'src/utils/pluralize';

type Props = {
  list: HumboList;
  reviews: Review[];
  visitedStats: VisitedStats;
  places: Place[];
  map: MapboxMap | null;
};

const VisitedPlacesMapSideContent = ({list, places, reviews, visitedStats, map}: Props) => {
  const [activeTab, setActiveTab] = useSearchParam('tab', 'countries');
  const [selectedPlaceQuery] = useSearchParam('selectedPlace');
  const [placesListQuery] = useSearchParam('places-list');

  return (
    <div
      className={twJoin('relative h-0 max-[800px]:h-auto grow w-full', selectedPlaceQuery && 'max-[800px]:opacity-0')}
    >
      <div
        className={twJoin(
          'flex flex-col gap-5 max-[800px]:gap-2 h-full',
          placesListQuery ? 'opacity-0 max-[800px]:h-0 overflow-hidden' : 'max-[800px]:h-auto'
        )}
      >
        <div className="pr-6 max-[800px]:pr-4">
          <TabBar>
            <TabButton
              size="small"
              active={activeTab === 'countries'}
              onPress={() => {
                setActiveTab('');
                setSearchParam('selectedPlace', '');
                setSearchParam('country', '');
                setSearchParam('placeType', '');
              }}
            >
              Countries
            </TabButton>

            <TabButton size="small" active={activeTab === 'places'} onPress={() => setActiveTab('places')}>
              {list.places.length} {pluralize('Place', list.places.length)}
            </TabButton>
          </TabBar>
        </div>

        <AnimatePresence mode="wait" initial={false}>
          {activeTab === 'countries' ? (
            <MapCountriesStats key="countries" list={list} reviews={reviews} visitedStats={visitedStats} map={map} />
          ) : (
            <MapReviewedPlacesList key="places" places={places} reviews={reviews} showSortControl map={map} />
          )}
        </AnimatePresence>
      </div>

      <AnimatePresence initial={false}>
        {placesListQuery ? <PlaceListModal reviews={reviews} map={map} /> : null}
      </AnimatePresence>
    </div>
  );
};

export default VisitedPlacesMapSideContent;
